import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { api } from "../../actions/api";
import { toast } from "react-toastify";
import CommentInput from "./CommentImput";
import useOutsideClick from "../../hooks/useOutsideClick";
import linkifyHtml from "linkify-html";
import parse from "html-react-parser";
import ReactionButton from "./ReactionButton";
import useReactions from "../../hooks/useReactions";
import { useQueryClient } from "@tanstack/react-query";
import { ReactionSummary } from "./ReactionSummary";
import ChatEmbedMedia from "../Chat/ChatEmbedMedia.jsx";
import {
  createParsedHtmlParseOptions,
  extractHostedVideoEmbeds,
} from "../../Utils/parseLinkifiedHtmlWithVideoEmbeds";

const StaticMessageCard = ({
  comment,
  userProfile,
  onReplyPosted,
  refreshMainComment,
  onCommentEdited,
  onLikeUpdated,
  members = [],
  isReply = false,
  onUserClicked,
  groupId,
}) => {
  // local comment state, used when parent doesn't control it
  const [comments, setComments] = useState(comment);
  useEffect(() => {
    setComments(comment);
  }, [comment]);

  const [commentRepliesData, setCommentRepliesData] = useState({
    replies: comment?.replys ?? [],
    meta: null,
  });

  const [liked, setLiked] = useState(comments.liked_by_current_user || false);
  const [likeCount, setLikeCount] = useState(comments.likes_count || 0);
  const { comments_count } = comments;
  const [commentsCount, setCommentsCount] = useState(comments_count);
  useEffect(() => {
    setCommentsCount(comments_count);
  }, [comments]);

  // Reactions state
  const [commentReactions, setCommentReactions] = useState(
    comments.reactions || null,
  );
  const { reactions: availableReactions, isReady: reactionsReady } =
    useReactions();
  const queryClient = useQueryClient();

  // Update reactions when comment changes
  useEffect(() => {
    if (comments.reactions) {
      setCommentReactions(comments.reactions);
      // Also update legacy like state if reactions exist
      const currentReaction = comments.reactions.current_user_reaction;
      if (currentReaction) {
        setLiked(true);
      }
    } else {
      // Fallback to legacy like state if no reactions data
      setLiked(comments.liked_by_current_user || false);
      setLikeCount(comments.likes_count || 0);
    }
  }, [
    comments.reactions,
    comments.liked_by_current_user,
    comments.likes_count,
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [showingReplies, setShowingReplies] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const dropdownRef = useRef(null);
  const commentTextRef = useRef(null);
  const [isCommentTextTruncated, setIsCommentTextTruncated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const lastReplyRef = useRef(null); // Ref to measure the last direct reply's position

  const linkConvert = (content) => {
    return linkifyHtml(content, {
      target: "_blank",
      rel: "noopener noreferrer",
    });
  };

  const mentionParseOptions = createParsedHtmlParseOptions(
    (domNode) => {
      if (
        domNode.name === "span" &&
        domNode.attribs?.class?.includes?.("comment-mention")
      ) {
        const userId = domNode.attribs["data-user-id"];
        const text =
          (domNode.children || [])
            .map((c) => (c.type === "text" ? c.data : ""))
            .join("") || "@";
        return (
          <span
            role="button"
            tabIndex={0}
            className="comment-mention"
            onClick={() => onUserClicked?.(Number(userId), null, true, groupId)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onUserClicked?.(Number(userId), null, true, groupId);
              }
            }}
          >
            {text}
          </span>
        );
      }
      return undefined;
    },
    { keepLinkAnchorsInline: true },
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    const diffInHours = Math.abs(now - date) / 36e5;
    const diffInMinutes = Math.abs(now - date) / (1000 * 60);

    if (diffInHours < 24) {
      if (diffInHours < 1) {
        // Less than 1 hour, show minutes
        const minutes = Math.floor(diffInMinutes);
        return `${minutes} minutes ago`;
      } else {
        // Between 1-24 hours, show hours
        const hours = Math.floor(diffInHours);
        return `${hours} hours ago`;
      }
    } else {
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
  };

  useOutsideClick(dropdownRef, () => setShowDropdown(false));

  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  const formatLikes = (num) => {
    if (!num) return null;
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  // Handle reaction update (called by ReactionButton)
  const handleReactionUpdate = useCallback((reactionsData) => {
    if (reactionsData) {
      // Force state update with new object reference to ensure React detects the change
      setCommentReactions({ ...reactionsData });
      // Update legacy like state for backward compatibility
      const hasReaction = reactionsData.current_user_reaction !== null;
      setLiked(hasReaction);
      setLikeCount(reactionsData.total_count || 0);
    }
  }, []);

  // Legacy toggle like function (kept for backward compatibility if needed)
  const toggleLike = async () => {
    try {
      setIsLoading(true);
      await api.post(`user/comment/like?comment_id=${comments.id}`);
      setLiked(!liked);
      setLikeCount((prev) => (liked ? prev - 1 : prev + 1));
    } catch (error) {
      console.error("Error toggling like:", error);
      toast.error("Failed to toggle like. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditComment = () => {
    setIsReplying(false);
    setIsEditing(true);
  };

  const handleDeleteComment = async () => {
    try {
      setIsLoading(true);
      await api.delete(`user/delete-comment/${comment.id}`);

      onReplyPosted?.({ deleting: true });
      refreshMainComment?.({ deleting: true });

      toast.success("Comment Deleted successfully!");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshComment = async ({ deleting = false } = {}) => {
    if (deleting) {
      setCommentsCount((prev) => prev - 1);
    } else {
      setCommentsCount((prev) => prev + 1);
    }

    onReplyPosted?.({ deleting });
    refreshMainComment?.({ deleting });

    loadCommentReplys();
    setIsReplying(false);
  };

  const refreshEditComment = () => {
    onCommentEdited?.();
    refreshMainComment?.({ isEditing: true });

    loadCommentReplys();
    setIsEditing(false);
  };

  const handleCommentPressed = () => {
    setIsEditing(false);
    setIsReplying((prev) => !prev);
    setShowingReplies(false);
  };

  const loadCommentReplys = async () => {
    try {
      setIsLoading(true);
      const res = await api.get(`user/comment/replies`, {
        params: { comment_id: comment.id, per_page: 10, page: 1 },
      });

      setCommentRepliesData({
        replies: res.data.data.comments,
        meta: res.data.data.meta,
      });
    } catch (error) {
      console.error("Error fetching replies:", error);
      toast.error("Failed to fetch comment replies. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreReplies = async () => {
    if (
      !loadingReplies &&
      commentRepliesData.meta?.current_page &&
      commentRepliesData.meta?.last_page &&
      commentRepliesData.meta.current_page < commentRepliesData.meta.last_page
    ) {
      try {
        setLoadingReplies(true);
        const nextPage = commentRepliesData.meta.current_page + 1;
        const params = {
          comment_id: comment.id,
          per_page: 10,
          page: nextPage,
        };

        const res = await api.get(`user/comment/replies`, {
          params,
        });

        setCommentRepliesData((prev) => ({
          ...prev,
          replies: [...prev.replies, ...res.data.data.comments],
          meta: res.data.data.meta,
        }));
      } catch (error) {
        console.error("Error fetching more replies:", error);
        toast.error("Failed to fetch more replies. Please try again.");
      } finally {
        setLoadingReplies(false);
      }
    }
  };

  useEffect(() => {
    loadCommentReplys();
  }, []);

  const commentText = comments.text ?? "";
  const commentLinked = commentText ? linkConvert(commentText) : "";
  const {
    videoUrls: commentVideoUrls,
    linkUrls: commentLinkUrls,
    textHtml: commentTextHtml,
    hasText: commentHasText,
  } = commentText
    ? extractHostedVideoEmbeds(commentLinked, { keepLinkAnchorsInline: true })
    : {
        videoUrls: [],
        linkUrls: [],
        textHtml: "",
        hasText: false,
      };
  const commentHasEmbeds =
    commentVideoUrls.length > 0 || commentLinkUrls.length > 0;

  useLayoutEffect(() => {
    if (expanded) {
      setIsCommentTextTruncated(false);
      return;
    }
    const el = commentTextRef.current;
    if (!el) return;
    setIsCommentTextTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [commentTextHtml, expanded, commentHasText]);

  const shouldShowCommentToggle = expanded || isCommentTextTruncated;

  useEffect(() => {
    setExpanded(false);
  }, [comments.id]);

  // Effect to adjust vertical line height to stop at last direct reply's avatar center
  // Only extends to direct replies, not nested ones
  const verticalLineRef = useRef(null);
  const repliesContainerRef = useRef(null);

  const updateVerticalLineHeight = useCallback(() => {
    if (
      showingReplies &&
      commentRepliesData.replies.length > 0 &&
      verticalLineRef.current &&
      lastReplyRef.current
    ) {
      // Use requestAnimationFrame and setTimeout to ensure DOM has fully updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          const containerRect =
            verticalLineRef.current.parentElement?.getBoundingClientRect();
          const lastReplyRect = lastReplyRef.current.getBoundingClientRect();
          if (containerRect && lastReplyRect) {
            // Calculate height to the center of the last direct reply's avatar
            // Avatar center is at top + 14px (half of 28px avatar)
            const avatarCenterY = lastReplyRect.top + 14 - containerRect.top;
            const lineHeight = avatarCenterY - 42; // 42px is top offset from parent avatar
            if (lineHeight > 0) {
              verticalLineRef.current.style.height = `${lineHeight}px`;
              verticalLineRef.current.style.bottom = "auto";
            }
          }
        }, 0);
      });
    }
  }, [showingReplies, commentRepliesData.replies, commentRepliesData.meta]);

  useEffect(() => {
    updateVerticalLineHeight();
  }, [updateVerticalLineHeight]);

  // Also recalculate when window is resized or when nested replies might change
  useEffect(() => {
    if (showingReplies && commentRepliesData.replies.length > 0) {
      const handleResize = () => {
        updateVerticalLineHeight();
      };
      window.addEventListener("resize", handleResize);
      // Use MutationObserver to detect when nested replies are added/removed
      const observer = new MutationObserver(() => {
        updateVerticalLineHeight();
      });

      if (repliesContainerRef.current) {
        observer.observe(repliesContainerRef.current, {
          childList: true,
          subtree: true,
          attributes: false,
        });
      }

      return () => {
        window.removeEventListener("resize", handleResize);
        observer.disconnect();
      };
    }
  }, [
    showingReplies,
    commentRepliesData.replies.length,
    updateVerticalLineHeight,
  ]);

  return (
    <div className="d-flex flex-column gap-2" style={{ position: "relative" }}>
      {/* Vertical connector line - starts from below parent comment's avatar, extends down to last reply (including nested) */}
      {commentRepliesData.replies.length > 0 && showingReplies && (
        <div
          ref={verticalLineRef}
          style={{
            position: "absolute",
            left: "14px",
            top: "42px",
            width: "2px",
            backgroundColor: "#008cff",
            zIndex: 0,
            pointerEvents: "none",
            transition: "height 0.2s ease",
          }}
        />
      )}
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex flex-column gap-2 flex-grow-1">
          <div className="d-flex align-items-start " style={{ gap: "13.5px" }}>
            <img
              src={comments.user?.image ?? "icons/chat-liam.svg"}
              alt="User"
              style={{ width: "28px", height: "28px", borderRadius: "50%" }}
            />
            <div className="d-flex flex-column gap-2 flex-grow-1">
              <div className="d-flex align-items-center gap-3 position-relative">
                <span
                  className="comment-tribe-text"
                  style={{ color: "#E6ECF1" }}
                >
                  {comments.user?.full_name}
                </span>
                <span className="text-caption-c2" style={{ color: "#D0D8DF" }}>
                  {formatDate(comments.created_at)}
                </span>

                <div
                  className="d-flex align-items-center position-relative"
                  ref={dropdownRef}
                >
                  {comments.user?.id === userProfile?.id && (
                    <>
                      <img
                        alt="three-dots"
                        src="icons/three-dots-post.svg"
                        style={{ cursor: "pointer" }}
                        onClick={toggleDropdown}
                      />

                      {showDropdown && (
                        <div
                          className="dropdown-menu"
                          style={{
                            position: "absolute",
                            top: "100%",
                            right: 0,
                            backgroundColor: "#1C2A37",
                            border: "1px solid #2A4052",
                            borderRadius: "10px",
                            padding: "8px 0",
                            zIndex: 1000,
                            width: "190px",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <div
                              className="dropdown-item d-flex align-items-center justify-content-center px-3 py-2 text-white"
                              style={{
                                cursor: "pointer",
                                marginBottom: "4px",
                                transition: "background-color 0.2s ease",
                                width: "100%",
                              }}
                              onClick={() => {
                                handleEditComment();
                                setShowDropdown(false);
                              }}
                            >
                              <img
                                src={"/icons/edit-icon.svg"}
                                alt={"Edit Comment"}
                                style={{ marginRight: "8px", width: "18px" }}
                              />
                              Edit Comment
                            </div>

                            <div
                              className="dropdown-item d-flex align-items-center justify-content-center px-3 py-2 text-white"
                              style={{
                                cursor: "pointer",
                                transition: "background-color 0.2s ease",
                                width: "100%",
                              }}
                              onClick={() => {
                                handleDeleteComment();
                                setShowDropdown(false);
                              }}
                            >
                              <img
                                src={"/icons/delete-icon.svg"}
                                alt={"Delete Comment"}
                                style={{ marginRight: "8px", width: "18px" }}
                              />
                              Delete Comment
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-column gap-1 tribe-comment-content">
                  {commentHasEmbeds && (
                    <ChatEmbedMedia
                      videoUrls={commentVideoUrls}
                      linkUrls={commentLinkUrls}
                    />
                  )}
                  {commentHasText && (
                    <div
                      className="text-body-b2 goals-text-message-part"
                      style={{
                        backgroundColor: "#121E29",
                        marginTop: "0px",
                        fontSize: "14px",
                        width: "fit-content",
                        wordBreak: "break-word",
                      }}
                    >
                      <div
                        ref={commentTextRef}
                        className={`comment-text-content${
                          expanded ? "" : " comment-text-content--collapsed"
                        }`}
                        style={{
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                        }}
                      >
                        {parse(commentTextHtml, mentionParseOptions)}
                      </div>
                      {shouldShowCommentToggle && (
                        <div
                          className="d-flex align-items-center"
                          style={{ marginTop: "8px", cursor: "pointer" }}
                          onClick={() => setExpanded(!expanded)}
                        >
                          <svg
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath={`url(#clip-comment-${comments.id})`}>
                              <path
                                d={
                                  expanded
                                    ? "M12.6663 7.35547H3.33301V8.6888H12.6663V7.35547Z"
                                    : "M12.6663 8.6888H8.66634V12.6888H7.33301V8.6888H3.33301V7.35547H7.33301V3.35547H8.66634V7.35547H12.6663V8.6888Z"
                                }
                                fill="#008CFF"
                              />
                            </g>
                            <defs>
                              <clipPath id={`clip-comment-${comments.id}`}>
                                <rect
                                  width="16"
                                  height="16"
                                  fill="white"
                                  transform="translate(0 0.0214844)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#008CFF",
                              marginLeft: "8px",
                            }}
                          >
                            {expanded ? "See Less" : "See More"}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Reaction Summary - Display all reactions with counts */}
                  <ReactionSummary
                    reactionsData={commentReactions}
                    availableReactions={availableReactions}
                  />
                  <div className="d-flex gap-2 gap-sm-3 ">
                    {/* Reaction button */}
                    {reactionsReady ? (
                      <ReactionButton
                        type="comment"
                        targetId={comments.id}
                        reactionsData={commentReactions}
                        availableReactions={availableReactions}
                        onReactionUpdate={handleReactionUpdate}
                        formatLikes={formatLikes}
                      />
                    ) : (
                      // Fallback to legacy like button while reactions are loading
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "6px", cursor: "pointer" }}
                        onClick={() => {
                          toggleLike();
                        }}
                      >
                        <img
                          src={
                            liked
                              ? "icons/post-liked.svg"
                              : "icons/post-like.svg"
                          }
                          alt={liked ? "liked" : "like"}
                          style={{ width: "24px", height: "24px" }}
                        />
                        <span
                          style={{ color: "#D0D8DF" }}
                          className="counts-text"
                        >
                          {formatLikes(likeCount)}
                        </span>
                      </div>
                    )}

                    <div
                      className="d-flex align-items-center"
                      style={{ gap: "6px", cursor: "pointer" }}
                      onClick={handleCommentPressed}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 25 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19.9 19.11C16.84 22.17 12.32 22.83 8.61 21.12C8.06 20.89 7.62 20.72 7.19 20.72C6.01 20.72 4.53 21.87 3.76 21.11C2.99 20.34 4.15 18.86 4.15 17.67C4.15 17.24 3.97 16.8 3.75 16.25C2.04 12.55 2.7 8.02 5.76 4.97C9.66 1.07 16 1.07 19.9 4.97C23.81 8.88 23.8 15.21 19.9 19.11Z"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.77 12.45H16.78"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12.76 12.45H12.77"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.75 12.45H8.76"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        style={{ color: "#D0D8DF" }}
                        className="counts-text"
                      >
                        Reply
                      </span>
                    </div>
                  </div>
                </div>

                {/* replies + CommentInput unchanged */}
                {commentRepliesData.replies.length > 0 && (
                  <div className="d-flex flex-column gap-2">
                    {showingReplies && (
                      <div
                        ref={repliesContainerRef}
                        className="d-flex flex-column gap-2"
                        style={{
                          position: "relative",
                          marginLeft: "0px", // No margin, replies align with parent
                        }}
                      >
                        {commentRepliesData.replies.map((reply, index) => {
                          const isLastReply =
                            index === commentRepliesData.replies.length - 1;
                          const hasMorePages =
                            commentRepliesData.meta &&
                            commentRepliesData.meta.current_page <
                              commentRepliesData.meta.last_page;

                          return (
                            <div
                              key={reply.id}
                              ref={isLastReply ? lastReplyRef : null}
                              style={{
                                position: "relative",
                              }}
                            >
                              {/* Curved horizontal connector line - extends from vertical line to reply's avatar */}
                              <svg
                                style={{
                                  position: "absolute",
                                  left: "-31px", // Adjusted to 0 to align with the start of the path
                                  top: "9px", // Adjusted to 0 to align with the start of the path
                                  width: "27.5px", // Use these to define the overall space
                                  height: "20px",
                                  zIndex: 0,
                                  pointerEvents: "none",
                                  overflow: "visible",
                                }}
                                viewBox="0 0 27.5 28"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M 0 0 C 0 20, 20 20, 27.5 14"
                                  stroke="#008cff"
                                  strokeWidth="2"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <StaticMessageCard
                                userProfile={userProfile}
                                comment={reply}
                                members={members}
                                onReplyPosted={refreshComment}
                                onCommentEdited={refreshEditComment}
                                onUserClicked={onUserClicked}
                                groupId={groupId}
                              />
                            </div>
                          );
                        })}

                        {commentRepliesData.meta &&
                          commentRepliesData.meta.current_page <
                            commentRepliesData.meta.last_page && (
                            <p
                              className="mb-0"
                              style={{
                                color: "rgb(0, 140, 255)",
                                cursor: "pointer",
                                fontSize: "14px",
                                marginLeft: "20px",
                              }}
                              onClick={loadMoreReplies}
                            >
                              {loadingReplies
                                ? "Loading more replies..."
                                : "Load more replies..."}
                            </p>
                          )}
                      </div>
                    )}

                    <p
                      className="mb-0"
                      style={{
                        color: "rgb(0, 140, 255)",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                      onClick={() => setShowingReplies((prev) => !prev)}
                    >
                      {showingReplies
                        ? `Hide replies(${commentsCount})`
                        : `Show replies(${commentsCount})`}
                    </p>
                  </div>
                )}

                {(isReplying || isEditing) && (
                  <CommentInput
                    postId={comment.id}
                    groupId={groupId}
                    userProfile={userProfile}
                    onCommentPosted={
                      isEditing ? refreshEditComment : refreshComment
                    }
                    replying={true}
                    replyToUser={!isEditing ? comments.user : null}
                    members={members}
                    isEditing={isEditing}
                    commentText={comments.text}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaticMessageCard;
